<template>
    <div class="qi-page">
        <a href="/quotes" class="btn btn-sm btn-outline-secondary mb-2"><i class="bi bi-arrow-left"></i> Back to Quotes</a>

        <div v-if="loading" class="text-center py-5"><span class="spinner-border"></span></div>

        <div v-else-if="quote">
            <div class="qi-doc-titlebar d-flex justify-content-between flex-wrap align-items-start">
                <h4 class="mb-0">{{ company.name }} <span class="text-muted">#{{ quote.number }}</span></h4>
                <div class="qi-header-actions" style="gap:8px;">
                    <div class="dropdown">
                        <button class="btn btn-outline-primary rounded dropdown-toggle" type="button" data-toggle="dropdown" aria-expanded="false">More actions</button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <button type="button" class="dropdown-item" :disabled="downloadingPdf" @click="downloadPdfFile"><i class="bi bi-file-earmark-pdf mr-2"></i>Export as PDF</button>
                            <button type="button" class="dropdown-item" :disabled="printingPdf" @click="printPdfFile"><i class="bi bi-printer mr-2"></i>Print</button>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" href="/settings/receipt"><i class="bi bi-palette mr-2"></i>Customize</a>
                            <button type="button" class="dropdown-item" @click="showPreview = true"><i class="bi bi-eye mr-2"></i>Preview as customer</button>
                            <a v-if="canEdit" class="dropdown-item" :href="`/quotes/${quote.id}/edit`"><i class="bi bi-pencil mr-2"></i>Edit quote</a>
                            <template v-if="canCancel">
                                <div class="dropdown-divider"></div>
                                <button type="button" class="dropdown-item text-danger" @click="cancelQuote"><i class="bi bi-x-circle mr-2"></i>Cancel quote</button>
                            </template>
                        </div>
                    </div>
                    <a href="/quotes/create" class="btn btn-outline-primary rounded">Create another Quote</a>
                </div>
            </div>

            <div class="qi-doc-meta-row">
                <div>
                    <label class="qi-mini-label">Status</label>
                    <span class="badge" :class="statusMeta.badge">{{ statusMeta.label }}</span>
                </div>
                <div>
                    <label class="qi-mini-label">Customer</label>
                    <div class="font-weight-bold">{{ customerLabel(quote.customer) }}</div>
                    <small v-if="customerAttnLine(quote.customer)" class="text-muted">Attn: {{ customerAttnLine(quote.customer) }}</small>
                    <small v-if="quote.customer && quote.customer.phone" class="text-muted d-block">{{ quote.customer.phone }}</small>
                    <small v-if="quote.customer && quote.customer.full_address" class="text-muted d-block">{{ quote.customer.full_address }}</small>
                </div>
                <div>
                    <label class="qi-mini-label">Total</label>
                    <div class="font-weight-bold">{{ formatMoney(quote.total_amount) }}</div>
                </div>
                <div v-if="quote.expiry_date">
                    <label class="qi-mini-label">Valid until</label>
                    <div class="font-weight-bold">{{ formatDate(quote.expiry_date) }}</div>
                </div>
            </div>

            <div class="qi-stepper">
                <div class="qi-step qi-step-done">
                    <div class="qi-step-icon"><i class="bi bi-file-earmark-text"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">Create</div>
                        <div class="text-muted small">Created {{ formatDate(quote.date) }}</div>
                    </div>
                </div>
                <div class="qi-step" :class="{ 'qi-step-done': quote.sent_at }">
                    <div class="qi-step-icon"><i class="bi bi-send"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">Send</div>
                        <div class="text-muted small">Last sent: {{ quote.sent_at ? formatDate(quote.sent_at) : 'Never' }}</div>
                    </div>
                    <button type="button" class="btn btn-primary btn-sm" @click="openSendModal"><i class="bi bi-send mr-1"></i>Send</button>
                </div>
                <div class="qi-step" :class="{ 'qi-step-done': quote.status === 'converted' }">
                    <div class="qi-step-icon"><i class="bi bi-receipt"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">Convert to Invoice</div>
                        <div class="text-muted small" v-if="quote.status === 'converted'">Converted</div>
                        <div class="text-muted small" v-else-if="quote.status === 'cancelled'">Quote cancelled</div>
                        <div class="text-muted small" v-else>Not yet converted</div>
                    </div>
                    <a v-if="quote.status === 'converted' && quote.converted_invoice_id" :href="`/invoices/${quote.converted_invoice_id}`" class="btn btn-outline-primary btn-sm">
                        View Invoice <i class="bi bi-arrow-right ml-1"></i>
                    </a>
                    <button v-else-if="quote.status !== 'cancelled'" type="button" class="btn btn-primary btn-sm" :disabled="converting" @click="convert">
                        <span v-if="converting" class="spinner-border spinner-border-sm mr-1"></span>
                        Convert to Invoice
                    </button>
                </div>
            </div>

            <div class="qi-form-card">
            <div class="qi-company-header">
                <div class="qi-company-brand">
                    <img v-if="showLogo && company.logo_url" :src="company.logo_url" :alt="company.name" class="qi-company-logo">
                    <div>
                        <div class="font-weight-bold">{{ company.name }}</div>
                        <small class="d-block font-weight-bold font-italic" v-if="company.companysettings && company.companysettings.document_tagline">{{ company.companysettings.document_tagline }}</small>
                        <small class="text-muted d-block" v-if="company.companysettings && company.companysettings.document_description">{{ company.companysettings.document_description }}</small>
                        <small class="text-muted d-block" v-if="company.phone || company.email">
                            <span v-if="company.phone">{{ company.phone }}</span><span v-if="company.phone && company.email"> &middot; </span><span v-if="company.email">{{ company.email }}</span>
                        </small>
                        <small class="text-muted d-block" v-if="companyAddress">{{ companyAddress }}</small>
                        <small class="text-muted d-block" v-if="company.krapin">PIN: {{ company.krapin }}</small>
                    </div>
                </div>
            </div>

            <div class="table-responsive d-none d-md-block">
                <table class="table table-sm qi-items-table">
                    <thead><tr><th>Item</th><th>Qty</th><th>Price</th><th>Discount</th><th>Tax</th><th class="text-right">Total</th></tr></thead>
                    <tbody>
                        <tr v-for="item in quote.items" :key="item.id">
                            <td>
                                {{ item.description }}
                                <small v-if="item.item_note" class="text-muted d-block">{{ item.item_note }}</small>
                            </td>
                            <td>{{ item.quantity }}</td>
                            <td>{{ formatMoney(item.unit_price) }}</td>
                            <td>{{ formatMoney(item.discount) }}</td>
                            <td>{{ itemTaxLabel(item) }}</td>
                            <td class="text-right">{{ formatMoney(item.total) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-md-none">
                <div class="qi-item-card" v-for="item in quote.items" :key="item.id">
                    <div class="d-flex justify-content-between">
                        <span class="font-weight-bold">{{ item.description }}</span>
                        <span class="font-weight-bold">{{ formatMoney(item.total) }}</span>
                    </div>
                    <div v-if="item.item_note" class="text-muted small">{{ item.item_note }}</div>
                    <div class="text-muted small">
                        {{ item.quantity }} &times; {{ formatMoney(item.unit_price) }}
                        <span v-if="item.discount > 0"> &middot; -{{ formatMoney(item.discount) }} discount</span>
                        <span v-if="itemTaxLabel(item) !== '—'"> &middot; {{ itemTaxLabel(item) }}</span>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-7">
                    <div v-if="quote.notes" class="mb-2"><label class="qi-mini-label">Notes</label><p class="mb-0">{{ quote.notes }}</p></div>
                    <div v-if="quote.terms"><label class="qi-mini-label">Terms</label><p class="mb-0">{{ quote.terms }}</p></div>
                </div>
                <div class="col-md-5">
                    <div class="qi-totals-box">
                        <div class="d-flex justify-content-between"><span>Subtotal</span><span>{{ formatMoney(quote.subtotal) }}</span></div>
                        <div class="d-flex justify-content-between text-muted"><span>Discount</span><span>-{{ formatMoney(quote.discount) }}</span></div>
                        <div class="d-flex justify-content-between text-muted"><span>Tax</span><span>{{ formatMoney(quote.tax_amount) }}</span></div>
                        <hr class="my-2">
                        <div class="d-flex justify-content-between font-weight-bold h6"><span>Total</span><span>{{ formatMoney(quote.total_amount) }}</span></div>
                    </div>
                </div>
            </div>
            </div>
        </div>

        <document-preview-modal
            v-if="showPreview"
            doc-type="Quote"
            :customer="quote.customer"
            :items="quote.items"
            :notes="quote.notes"
            :terms="quote.terms"
            :date="quote.date"
            :expiry-date="quote.expiry_date"
            :totals="{ subtotal: quote.subtotal, discount: quote.discount, tax: quote.tax_amount, total: quote.total_amount }"
            :currency="currency"
            saved
            @close="showPreview = false"
        ></document-preview-modal>

        <send-document-modal
            v-if="quote"
            ref="sendModal"
            doc-type="Quote"
            :doc-id="quoteId"
            :number="quote.number"
            :customer="quote.customer"
            @sent="onSent"
        ></send-document-modal>
    </div>
</template>

<script>
import DocumentPreviewModal from './DocumentPreviewModal.vue';
import SendDocumentModal from './SendDocumentModal.vue';
import { formatMoney, quoteStatusMeta, customerLabel, customerAttnLine, apiErrorMessage } from './docHelpers';
import { downloadPdf, printPdf } from '../../services/pdfDownload';

export default {
    name: 'QuoteView',
    components: { DocumentPreviewModal, SendDocumentModal },
    props: {
        quoteId: { type: String, required: true },
    },
    data() {
        return {
            loading: true,
            converting: false,
            showPreview: false,
            downloadingPdf: false,
            printingPdf: false,
            quote: null,
        };
    },
    computed: {
        currency() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings && u.company.companysettings.currency) || 'KSh';
        },
        company() {
            const u = this.$store.getters.userDetails;
            return (u && u.company) || {};
        },
        showLogo() {
            const cs = this.company.companysettings;
            return !cs || cs.document_show_logo !== false;
        },
        companyAddress() {
            return [this.company.building, this.company.street, this.company.address, this.company.town].filter(Boolean).join(', ');
        },
        statusMeta() { return quoteStatusMeta(this.quote ? this.quote.status : 'draft'); },
        canEdit() { return this.quote && this.quote.status !== 'converted' && this.quote.status !== 'cancelled'; },
        canCancel() { return this.quote && this.quote.status !== 'converted' && this.quote.status !== 'cancelled'; },
        pdfUrl() { return `/api/quotes/${this.quoteId}/pdf`; },
    },
    created() { this.load(); },
    methods: {
        customerLabel,
        customerAttnLine,
        formatMoney(v) { return formatMoney(v, this.currency); },
        formatDate(d) { return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
        itemTaxLabel(item) {
            if (item.tax_name) return `${item.tax_name} (${item.tax_rate}%)`;
            if (item.tax) return `${item.tax.tax} (${item.tax.tax_perc}%)`;
            return '—';
        },
        async load() {
            this.loading = true;
            try {
                const { data } = await axios.get(`/api/quotes/${this.quoteId}`);
                this.quote = data;
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: apiErrorMessage(e, 'Could not load this quote.') });
            } finally {
                this.loading = false;
            }
        },
        async convert() {
            if (this.converting || (this.quote && this.quote.status === 'converted')) return;
            this.converting = true;
            try {
                const { data } = await axios.post(`/api/quotes/${this.quoteId}/convert`);
                this.$toasts.push({ type: 'success', message: `Converted to invoice ${data.number}.` });
                window.location.href = `/invoices/${data.id}`;
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: apiErrorMessage(e, 'Could not convert this quote.') });
                this.converting = false;
                this.load();
            }
        },
        async cancelQuote() {
            if (!confirm('Cancel this quote? This cannot be undone.')) return;
            try {
                await axios.delete(`/api/quotes/${this.quoteId}`);
                this.$toasts.push({ type: 'success', message: 'Quote cancelled.' });
                this.load();
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: apiErrorMessage(e, 'Could not cancel this quote.') });
            }
        },
        openSendModal() {
            this.$refs.sendModal.open();
        },
        onSent(sentAt) {
            this.quote.sent_at = sentAt;
        },
        async downloadPdfFile() {
            this.downloadingPdf = true;
            try {
                await downloadPdf(this.pdfUrl, `Quote-${this.quote.number}.pdf`);
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: 'Could not download the PDF. Please try again.' });
            } finally {
                this.downloadingPdf = false;
            }
        },
        async printPdfFile() {
            this.printingPdf = true;
            try {
                await printPdf(this.pdfUrl);
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: 'Could not print this quote. Please try again.' });
            } finally {
                this.printingPdf = false;
            }
        },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
.qi-page { padding: 16px; max-width: 1000px; margin: 0 auto; }
@media (max-width: 768px) { .qi-page { padding-bottom: 90px; } }
.qi-form-card { background: #fff; border: 1px solid #eee; border-radius: 10px; padding: 20px; }
.qi-doc-titlebar { margin-bottom: 14px; }
.qi-header-actions { display: flex; flex-wrap: wrap; }
.qi-doc-meta-row {
    display: flex;
    gap: 40px;
    flex-wrap: wrap;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 14px 18px;
    margin-bottom: 14px;
}
.qi-stepper { display: flex; flex-direction: column; gap: 8px; margin-bottom: 18px; }
.qi-step {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 12px 16px;
}
.qi-step-icon {
    width: 34px; height: 34px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    background: #f1f2f4; color: #adb5bd; font-size: 1rem; flex-shrink: 0;
}
.qi-step-done .qi-step-icon { background: rgba(253,71,25,.12); color: #fd4719; }
.qi-step-body { flex: 1; }
.qi-step-title { font-weight: 600; font-size: .92rem; }
.qi-company-header { padding-bottom: 14px; margin-bottom: 14px; border-bottom: 1px solid #eee; }
.qi-company-brand { display: flex; align-items: center; gap: 12px; }
.qi-company-logo { height: 40px; width: auto; max-width: 140px; object-fit: contain; }
.qi-action-bar { display: flex; gap: 6px; flex-wrap: wrap; }
.qi-mini-label { font-size: .72rem; text-transform: uppercase; color: #6c757d; display: block; }
.qi-items-table th { font-size: .75rem; text-transform: uppercase; color: #6c757d; border-top: none; }
.qi-item-card { border: 1px solid #eee; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; }
.qi-totals-box { background: #f8f9fa; border-radius: 8px; padding: 14px 16px; }
</style>
